import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTeachers } from '../hooks/useWebsiteContent';
import { mockData } from '../data/mock';
import { Play, Loader2, Calendar } from 'lucide-react';
import TeacherVideoModal from './TeacherVideoModal';
import TeacherBookingModal from './TeacherBookingModal';

const TeachersSection = () => {
  const { language } = useLanguage();
  const { teachers, loading } = useTeachers();
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  const handleTeacherClick = (teacher) => {
    setSelectedTeacher(teacher);
    setVideoModalOpen(true);
  };

  const handleBookSession = (teacher, e) => {
    e?.stopPropagation();
    setSelectedTeacher(teacher);
    setBookingModalOpen(true);
  };

  const handleSchedule = (teacher) => {
    // Open booking modal instead of scrolling to contact
    setSelectedTeacher(teacher);
    setVideoModalOpen(false);
    setBookingModalOpen(true);
  };

  const content = {
    en: {
      title: "Our Amazing Teachers",
      subtitle: "Meet Our Professional Instructors",
      description: "Click to watch introduction videos and book your session",
      bookSession: "Book Session"
    },
    ar: {
      title: "معلمونا المحترفون",
      subtitle: "تعرف على مدرسينا المحترفين",
      description: "انقر لمشاهدة مقاطع التعريف وحجز جلستك",
      bookSession: "احجز جلسة"
    }
  };

  const t = content[language];

  return (
    <section id="teachers" className="py-20 bg-gradient-to-br from-slate-50 to-teal-50/30" aria-labelledby="teachers-heading">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 id="teachers-heading" className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">{t.title}</h2>
          <p className="text-xl text-gray-600 mb-2">{t.subtitle}</p>
          <p className="text-sm text-gray-500">{t.description}</p>
        </div>

        {/* Teachers Grid - 2 columns, 3 rows */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
          {loading ? (
            <div className="col-span-2 flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : (
          teachers.map((teacher) => (
            <div
              key={teacher.id}
              className="group cursor-pointer"
              onClick={() => handleTeacherClick(teacher)}
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                {/* Teacher Image with Play Overlay */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={
                      teacher.profile_image 
                        ? (teacher.profile_image.startsWith('data:') || teacher.profile_image.startsWith('http')
                            ? teacher.profile_image 
                            : `${process.env.REACT_APP_BACKEND_URL}${teacher.profile_image}`)
                        : (teacher.image || 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80')
                    }
                    alt={`${teacher.name} - ${teacher.title?.[language] || 'English Teacher'} at Canada English Life Academy`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-transform">
                      <Play className="w-10 h-10 text-white fill-white" />
                    </div>
                  </div>
                  
                  {/* Teacher Info Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-1">{teacher.name}</h3>
                    <p className="text-teal-300 font-medium">{teacher.title?.[language] || (language === 'en' ? 'English Teacher' : 'معلم لغة إنجليزية')}</p>
                  </div>
                </div>

                {/* Watch Video CTA */}
                <div className="p-4 flex">
                  <button
                    onClick={() => handleTeacherClick(teacher)}
                    className="flex-1 py-2 bg-teal-600 text-white text-center font-semibold hover:bg-teal-700 transition-colors duration-300 rounded-l-lg flex items-center justify-center space-x-2"
                  >
                    <Play className="w-4 h-4 fill-white" />
                    <span>{language === 'en' ? 'Watch Intro' : 'شاهد التعريف'}</span>
                  </button>
                  <button
                    onClick={(e) => handleBookSession(teacher, e)}
                    className="flex-1 py-2 bg-amber-500 text-white text-center font-semibold hover:bg-amber-600 transition-colors duration-300 rounded-r-lg flex items-center justify-center space-x-2"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>{t.bookSession}</span>
                  </button>
                </div>
              </div>
            </div>
          ))
          )}
        </div>

        {/* Teacher Video Modal */}
        <TeacherVideoModal
          teacher={selectedTeacher}
          isOpen={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          onSchedule={handleSchedule}
        />

        {/* Teacher Booking Modal */}
        <TeacherBookingModal
          teacher={selectedTeacher}
          open={bookingModalOpen}
          onClose={() => setBookingModalOpen(false)}
        />

        {/* Course Types Section */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wide">
              {language === 'en' ? 'English Courses' : 'دورات الإنجليزية'}
            </span>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2">
              {language === 'en' ? 'Types of English Courses' : 'أنواع دوراتنا الإنجليزية التعليمية'}
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {mockData.courseTypes.map((type) => (
              <div
                key={type.id}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="md:w-2/5 h-64 md:h-auto relative overflow-hidden">
                    <img
                      src={type.image}
                      alt={type.title[language]}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>

                  {/* Content */}
                  <div className="md:w-3/5 p-8 flex flex-col justify-center">
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">
                      {type.title[language]}
                    </h4>
                    <p className="text-teal-600 font-semibold text-lg mb-4">
                      {type.subtitle[language]}
                    </p>
                    <div className="w-16 h-1 bg-teal-500 rounded-full group-hover:w-24 transition-all duration-300"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom CSS for animation */}
      <style jsx>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .animate-scroll {
          animation: scroll 30s linear infinite;
        }

        .pause-animation:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

export default TeachersSection;
